// VideoLessons — an interactive, time-synced player for talk-style masterclasses.
//
// One big video up top, lesson sections below. As the video plays, the matching
// lesson highlights and follows along; clickable chapter markers seek the video;
// the player stays pinned (sticky) while you scroll the lessons; an optional
// transcript panel lets you jump anywhere.
//
// Built for long-form talks (the 30+ min ones). Pairs with the `videoLessons`
// block kind in MasterclassReader. Renders each segment's lesson blocks through
// the same renderBlock the rest of the reader uses, so callouts/principles/etc.
// all work inside a segment.
//
// Features (all five):
//   1. Time-synced lessons   — active segment highlights + follows playback
//   2. Clickable chapters     — chip rail seeks the video
//   3. Sticky player          — pinned while you scroll the lessons
//   4. Pause-and-reflect      — segments can carry reflection blocks (authored)
//   5. Jump-to transcript     — collapsible, click a line to seek

import React, { useEffect, useMemo, useRef, useState } from "react";
// LazyMuxPlayer, NOT @mux/mux-player-react directly — a static import here
// chains through MasterclassReader into the content chunk and re-inflates the
// boot bundle with the ~3MB video stack that LazyMuxPlayer exists to defer
// (a documented WKWebView watchdog crash risk; see LazyMuxPlayer.jsx).
import LazyMuxPlayer from "../components/LazyMuxPlayer";
import { setNowPlaying, setPlaybackState, setPositionState } from "../utils/nowPlaying";
import { ChevronDown, ListChecks } from "lucide-react";
import { GOLD } from "../constants/theme";

const fmt = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(s / 60), ss = s % 60;
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

// Parse a WebVTT string into [{ t, text }] cues. Tolerant of the auto-caption
// shape Mux produces (numeric cue ids, X-TIMESTAMP-MAP header, etc.).
function parseVtt(vtt) {
  const out = [];
  const blocks = vtt.replace(/\r/g, "").split("\n\n");
  const tc = (s) => {
    const m = s.match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
    if (!m) return null;
    return (+m[1]) * 3600 + (+m[2]) * 60 + (+m[3]) + (+m[4]) / 1000;
  };
  for (const b of blocks) {
    const lines = b.split("\n").filter(Boolean);
    const arrowLine = lines.find((l) => l.includes("-->"));
    if (!arrowLine) continue;
    const t = tc(arrowLine.split("-->")[0]);
    if (t == null) continue;
    const text = lines.slice(lines.indexOf(arrowLine) + 1).join(" ").trim();
    if (text) out.push({ t, text });
  }
  return out;
}

// variant="cards": each segment is a question with its answer, so it renders
// as a numbered card rather than a lesson section, and the chip rail counts
// them (Q1, Q2) instead of repeating the question text.
export function VideoLessons({ playbackId, segments = [], textTrackId, C, renderBlock, ctx, variant = "lessons", nowPlaying }) {
  const cards = variant === "cards";
  // State (not a ref) so the timeupdate effect re-runs when the lazy player
  // chunk resolves and the <mux-player> element actually mounts — with a
  // plain ref the effect ran once against null and never attached.
  const [playerEl, setPlayerEl] = useState(null);
  const segRefs = useRef([]);
  const chipRefs = useRef([]);
  const railRef = useRef(null);
  const [ct, setCt] = useState(0);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [cues, setCues] = useState(null); // null = not loaded, [] = none

  // Active segment = last one whose start time has passed.
  const activeIdx = useMemo(() => {
    let idx = 0;
    for (let i = 0; i < segments.length; i++) {
      if ((segments[i].t || 0) <= ct + 0.25) idx = i; else break;
    }
    return idx;
  }, [ct, segments]);

  // Listen to the player's timeupdate. mux-player is a web component, so we
  // attach the native listener to the underlying element via the ref.
  useEffect(() => {
    const el = playerEl;
    if (!el) return;
    let raf = 0;
    const onTime = () => {
      // Throttle to ~2/sec; we only need it for highlight/sync, not per-frame.
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; setCt(el.currentTime || 0); });
    };
    el.addEventListener("timeupdate", onTime);
    return () => { el.removeEventListener("timeupdate", onTime); if (raf) cancelAnimationFrame(raf); };
  }, [playerEl, playbackId]);

  // ── The lock screen ──────────────────────────────────────────────────
  //
  // Only long-form surfaces reach this component - the Weekly Q&A reader
  // and talk-style masterclasses - which is the whole scope of the
  // background-audio capability. A coach driving with a forty-five minute
  // Q&A on wants the title and a working pause on the lock screen; the
  // native half (Info.plist `audio` plus the AVAudioSession in
  // AppDelegate) keeps the sound alive, and this is what makes the screen
  // it shows worth looking at.
  //
  // mux-player is a custom element that implements the media interface
  // directly, so it can be handed over as the thing to play and pause.
  useEffect(() => {
    if (!playerEl || !nowPlaying?.title) return;
    const teardown = setNowPlaying(nowPlaying, () => playerEl);
    const onPlay  = () => setPlaybackState("playing");
    const onPause = () => setPlaybackState("paused");
    // Throttled to once a second: the scrubber moves in seconds, and
    // setPositionState on every timeupdate is work nobody sees.
    let lastPos = 0;
    const onTimePos = () => {
      const now = Date.now();
      if (now - lastPos < 1000) return;
      lastPos = now;
      setPositionState(playerEl);
    };
    playerEl.addEventListener("play", onPlay);
    playerEl.addEventListener("pause", onPause);
    playerEl.addEventListener("timeupdate", onTimePos);
    return () => {
      playerEl.removeEventListener("play", onPlay);
      playerEl.removeEventListener("pause", onPause);
      playerEl.removeEventListener("timeupdate", onTimePos);
      // Closing the reader must clear the lock screen. A paused episode
      // still sitting there after a coach has moved on is a control that
      // does nothing attached to a title that is no longer true.
      teardown();
    };
  }, [playerEl, nowPlaying]);

  // Track manual scrolling so follow-along never fights the reader — wheel /
  // touchmove are input events, so our own smooth scrollIntoView below doesn't
  // re-trigger this.
  const lastUserScroll = useRef(0);
  useEffect(() => {
    const mark = () => { lastUserScroll.current = Date.now(); };
    window.addEventListener("touchmove", mark, { passive: true });
    window.addEventListener("wheel", mark, { passive: true });
    return () => {
      window.removeEventListener("touchmove", mark);
      window.removeEventListener("wheel", mark);
    };
  }, []);

  // Follow-along: when the active segment changes, ease it into view — but not
  // within a few seconds of the user scrolling themselves (on a phone the
  // player and lessons share the viewport, so auto-scroll while reading ahead
  // yanked the page back on every segment change).
  useEffect(() => {
    if (Date.now() - lastUserScroll.current < 4000) return;
    const node = segRefs.current[activeIdx];
    if (node) node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const chip = chipRefs.current[activeIdx];
    if (chip && railRef.current) {
      const r = railRef.current, c = chip;
      if (c.offsetLeft < r.scrollLeft || c.offsetLeft + c.offsetWidth > r.scrollLeft + r.clientWidth) {
        r.scrollTo({ left: c.offsetLeft - 16, behavior: "smooth" });
      }
    }
  }, [activeIdx]);

  const seek = (t) => {
    const el = playerEl;
    if (!el) return;
    try { el.currentTime = t; const p = el.play?.(); if (p && p.catch) p.catch(() => {}); } catch {}
  };

  // Lazy-load the transcript only when the panel is first opened, and only if a
  // text track id was supplied. Best-effort; failure just hides the panel body.
  useEffect(() => {
    if (!transcriptOpen || cues !== null || !textTrackId || !playbackId) return;
    let cancelled = false;
    fetch(`https://stream.mux.com/${playbackId}/text/${textTrackId}.vtt`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((txt) => { if (!cancelled) setCues(parseVtt(txt)); })
      .catch(() => { if (!cancelled) setCues([]); });
    return () => { cancelled = true; };
  }, [transcriptOpen, cues, textTrackId, playbackId]);

  return (
    <div style={{ margin: "8px 0 4px" }}>
      {/* Sticky player — stays pinned while the lessons scroll under it. */}
      <div style={{ position: "sticky", top: 0, zIndex: 6, background: C.bg || "#0A0A0A", paddingBottom: 10 }}>
        <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${C.cardBorder}`, background: "#000", aspectRatio: "16 / 9" }}>
          <LazyMuxPlayer
            ref={setPlayerEl}
            playbackId={playbackId}
            streamType="on-demand"
            accentColor={GOLD}
            style={{ width: "100%", height: "100%", "--controls-backdrop-color": "rgba(0,0,0,0.4)" }}
          />
        </div>
        {/* Chapter chip rail — click to jump. */}
        {segments.length > 0 && (
          <div ref={railRef} style={{ display: "flex", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", marginTop: 10, paddingBottom: 4, scrollbarWidth: "none" }}>
            {segments.map((s, i) => {
              const on = i === activeIdx;
              return (
                <button
                  key={i}
                  ref={(n) => (chipRefs.current[i] = n)}
                  onClick={() => seek(s.t)}
                  style={{
                    flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "6px 11px", borderRadius: 999, cursor: "pointer",
                    border: `1px solid ${on ? GOLD : C.cardBorder}`,
                    background: on ? `${GOLD}1A` : "transparent",
                    color: on ? GOLD : C.textMid, fontSize: 12, fontWeight: 700,
                    fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap",
                  }}
                >
                  {cards ? <><span style={{ fontWeight: 900 }}>Q{i + 1}</span><span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.8, fontSize: 11 }}>{fmt(s.t)}</span></>
                    : <><span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.8, fontSize: 11 }}>{fmt(s.t)}</span>{s.title}</>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Lesson sections — each follows the video. */}
      <div style={{ marginTop: 8 }}>
        {segments.map((s, i) => {
          const on = i === activeIdx;
          if (cards) return (
            <section
              key={i}
              ref={(n) => (segRefs.current[i] = n)}
              style={{
                scrollMarginTop: 12, margin: "12px 0", padding: "16px 18px 10px",
                borderRadius: 12, border: `1px solid ${on ? `${GOLD}66` : C.cardBorder}`,
                background: on ? `${GOLD}0A` : (C.cardGradient || "transparent"),
                boxShadow: on ? `inset 3px 0 0 ${GOLD}` : "none",
                transition: "background .25s ease, border-color .25s ease",
              }}
            >
              <button
                onClick={() => seek(s.t)}
                title="Jump the video here"
                style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}
              >
                <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, letterSpacing: 1, color: on ? GOLD : C.textDim, lineHeight: 1 }}>Q{i + 1}</span>
                  <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 800, color: on ? GOLD : C.textDim, border: `1px solid ${on ? GOLD : C.cardBorder}`, borderRadius: 6, padding: "2px 7px" }}>{fmt(s.t)}</span>
                </span>
                <span style={{ fontSize: 16.5, fontWeight: 800, color: C.text, lineHeight: 1.3, letterSpacing: -0.1 }}>{s.title}</span>
              </button>
              {(s.blocks || []).map((b, j) => (
                <div key={j}>{renderBlock(b, ctx)}</div>
              ))}
            </section>
          );
          return (
            <section
              key={i}
              ref={(n) => (segRefs.current[i] = n)}
              style={{
                scrollMarginTop: 12,
                padding: "16px 16px 6px 18px",
                margin: "14px 0",
                borderLeft: `3px solid ${on ? GOLD : C.cardBorder}`,
                background: on ? `${GOLD}0A` : "transparent",
                borderRadius: "0 10px 10px 0",
                transition: "background .25s ease, border-color .25s ease",
              }}
            >
              <button
                onClick={() => seek(s.t)}
                title="Jump the video here"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: 6 }}
              >
                <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 800, color: on ? GOLD : C.textDim, border: `1px solid ${on ? GOLD : C.cardBorder}`, borderRadius: 6, padding: "2px 7px" }}>{fmt(s.t)}</span>
                <span style={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: 0.2 }}>{s.title}</span>
              </button>
              {(s.blocks || []).map((b, j) => (
                <div key={j}>{renderBlock(b, ctx)}</div>
              ))}
            </section>
          );
        })}
      </div>

      {/* Jump-to transcript */}
      {textTrackId && (
        <div style={{ margin: "18px 0 4px", border: `1px solid ${C.cardBorder}`, borderRadius: 12, overflow: "hidden", background: C.cardGradient }}>
          <button
            onClick={() => setTranscriptOpen((o) => !o)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "13px 15px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
          >
            <ListChecks size={15} color={GOLD} strokeWidth={2.5} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: GOLD }}>Full transcript</span>
            <span style={{ fontSize: 12, color: C.textDim }}>jump to any moment</span>
            <ChevronDown size={16} color={C.textMid} style={{ marginLeft: "auto", transform: transcriptOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
          </button>
          {transcriptOpen && (
            <div style={{ maxHeight: 320, overflowY: "auto", padding: "0 8px 10px" }}>
              {cues === null && (
                <div style={{ padding: 14 }} aria-label="Loading transcript">
                  {[78, 62, 84, 55].map((w, i) => (
                    <div key={i} style={{ height: 12, width: `${w}%`, borderRadius: 6, background: "rgba(255,255,255,0.06)", marginBottom: 10, animation: "profSkelPulse 1.3s ease-in-out infinite", animationDelay: `${i * 0.12}s` }} />
                  ))}
                  <style>{`@keyframes profSkelPulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }`}</style>
                </div>
              )}
              {cues && cues.length === 0 && <div style={{ padding: 14, fontSize: 13, color: C.textDim }}>Transcript unavailable.</div>}
              {cues && cues.map((cue, i) => (
                <button
                  key={i}
                  className="btn-inline bam-transcript-row"
                  onClick={() => seek(cue.t)}
                  style={{ display: "flex", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: "6px 8px", borderRadius: 6 }}
                >
                  <span style={{ flexShrink: 0, fontVariantNumeric: "tabular-nums", fontSize: 11, color: GOLD, opacity: 0.8, paddingTop: 2 }}>{fmt(cue.t)}</span>
                  <span style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5 }}>{cue.text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
